/**
 * Supplier payments through Wise (Fan, 2026-09-27).
 *
 * Open supplier invoices are listed with the Wise recipient we paid them on
 * before. "Draft payment" prepares it; "Approve and pay" sends it from the
 * Wise balance in the same currency. Nothing is paid without that click.
 * When Wise refuses to let the app fund it (403), the transfer waits in Wise
 * ("In Wise, pay it there") and is marked paid once it is paid in Wise.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { wisePayoutsAPI } from '../lib/api';
import { formatCurrency } from '../lib/chartFormatters';
import { getErrorMessage } from '../lib/errors';

const STATUS = {
  draft: { label: 'Waiting for approval', variant: 'secondary' },
  scheduled: { label: 'Approved, dated', variant: 'default' },
  paying: { label: 'Paying', variant: 'secondary' },
  in_wise: { label: 'In Wise: pay it there', variant: 'secondary' },
  paid: { label: 'Paid', variant: 'default' },
  failed: { label: 'Failed', variant: 'destructive' },
  cancelled: { label: 'Cancelled', variant: 'outline' },
};

export function SupplierPayments() {
  const [status, setStatus] = useState(null);
  const [sugg, setSugg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [manual, setManual] = useState({ recipient_id: '', amount: '', reference: '' });
  const [hidePaid, setHidePaid] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, g] = await Promise.all([
        wisePayoutsAPI.getStatus(),
        wisePayoutsAPI.getSuggestions(),
      ]);
      setStatus(s.data);
      setSugg(g.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const draft = async (body, key) => {
    setBusyId(key);
    try {
      await wisePayoutsAPI.createDraft(body);
      toast.success('Payment drafted. Approve it below to pay.');
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const approve = async (d) => {
    const when = d.pay_on ? `on ${d.pay_on}` : 'now';
    const ok = window.confirm(
      `Pay ${d.recipient_name} ${formatCurrency(Number(d.amount), d.currency)} ${when} from Wise?\n` +
      `Reference: ${d.reference}\nAccount: ${d.recipient_account || ''}`,
    );
    if (!ok) return;
    setBusyId(d.id);
    try {
      const res = await wisePayoutsAPI.approve(d.id);
      if (res.data.status === 'paid') toast.success('Paid');
      else if (res.data.status === 'in_wise') toast.warning('Created in Wise. Wise does not let the app pay it: pay it in the Wise app.');
      else if (res.data.status === 'scheduled') toast.success(`Approved, pays on ${res.data.pay_on}`);
      else toast.error(res.data.error || 'Payment failed');
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const cancel = async (d) => {
    if (d.steps?.transfer_id && !window.confirm(`Cancel Wise transfer ${d.steps.transfer_id} for ${d.supplier}?`)) return;
    setBusyId(d.id);
    try {
      await wisePayoutsAPI.cancel(d.id);
      await load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const invoices = useMemo(() => {
    const rows = (sugg?.invoices || []).filter((i) => !i.draft);
    return hidePaid ? rows.filter((i) => !i.looks_paid) : rows;
  }, [sugg, hidePaid]);

  const recipients = useMemo(
    () => [...(sugg?.recipients || [])].filter((r) => r.active !== false)
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [sugg],
  );

  if (loading && !status) {
    return <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading payments</div>;
  }

  const drafts = status?.drafts || [];
  const open = drafts.filter((d) => ['draft', 'scheduled', 'paying', 'in_wise', 'failed'].includes(d.status));
  const done = drafts.filter((d) => ['paid', 'cancelled'].includes(d.status)).slice(0, 20);

  return (
    <div className="space-y-6 pt-2">
      {!status?.signing_key_set && (
        <Card className="border-amber-300">
          <CardContent className="pt-6 text-sm">
            Wise needs a signing key before it pays suppliers. Drafts work already; paying waits for the key.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Waiting for approval</CardTitle>
          <CardDescription>
            Paid from the Wise balance in the same currency. {status?.can_approve ? '' : 'Only Fan can approve.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {open.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing waiting.</p>
          ) : (
            <div className="divide-y">
              {open.map((d) => (
                <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <div className="font-medium">{d.supplier} <span className="text-muted-foreground">to {d.recipient_name}</span></div>
                    <div className="text-xs text-muted-foreground">
                      {d.pay_on ? `pays on ${d.pay_on} · ` : ''}{d.recipient_account} · ref {d.reference}{d.invoice_number ? ` · invoice ${d.invoice_number}` : ''} · drafted by {d.created_by}
                    </div>
                    {d.status === 'failed' && <div className="text-xs text-red-600 mt-1">{d.error}</div>}
                    {d.status === 'in_wise' && (
                      <div className="text-xs text-amber-700 mt-1">
                        Created in Wise (transfer {d.steps?.transfer_id}). Wise does not let the app pay it: pay it in the Wise app. It is marked paid here once Wise sends it.
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold tabular-nums">{formatCurrency(Number(d.amount), d.currency)}</span>
                    <Badge variant={STATUS[d.status]?.variant}>{STATUS[d.status]?.label}</Badge>
                    {status?.can_approve && !['paying', 'scheduled', 'in_wise'].includes(d.status) && (
                      <Button size="sm" disabled={busyId === d.id} onClick={() => approve(d)}>
                        {busyId === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (d.status === 'failed' ? 'Try again' : (d.pay_on ? 'Approve' : 'Approve and pay'))}
                      </Button>
                    )}
                    {d.status !== 'paying' && !d.steps?.transfer_id && (
                      <Button size="sm" variant="outline" disabled={busyId === d.id} onClick={() => cancel(d)}>Cancel</Button>
                    )}
                    {status?.can_approve && ['in_wise', 'failed'].includes(d.status) && d.steps?.transfer_id && !d.steps?.funded && (
                      <Button size="sm" variant="outline" disabled={busyId === d.id} onClick={() => cancel(d)}>Cancel in Wise</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Open supplier invoices</CardTitle>
          <CardDescription>
            Not yet matched to a bank payment in the books. Some may be paid from another account, so check before drafting.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <label className="flex items-center gap-2 text-sm mb-3">
            <input type="checkbox" checked={hidePaid} onChange={(e) => setHidePaid(e.target.checked)} />
            Hide invoices that look paid already
          </label>
          {invoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">No open invoices.</p>
          ) : (
            <div className="divide-y">
              {invoices.map((i) => (
                <div key={i.invoice_id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <div className="font-medium">{i.supplier}</div>
                    <div className="text-xs text-muted-foreground">
                      {i.invoice_number || 'no number'} · issued {i.issue_date}{i.due_date ? ` · due ${i.due_date}` : ''}
                      {i.recipient ? ` · pays to ${i.recipient.name} ${i.recipient.summary || ''}` : ' · no Wise recipient found'}
                      {i.recipient?.match === 'name' ? ' (matched by name, check)' : ''}
                    </div>
                    {i.looks_paid && (
                      <div className="text-xs text-amber-700 mt-1">
                        Looks paid: {i.looks_paid.counterparty} {i.looks_paid.date}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold tabular-nums">{formatCurrency(Number(i.amount), i.currency)}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!i.recipient || busyId === i.invoice_id}
                      onClick={() => draft({
                        invoice_id: i.invoice_id,
                        recipient_id: i.recipient.id,
                        amount: i.amount,
                        currency: i.currency,
                        reference: i.invoice_number || '',
                      }, i.invoice_id)}
                    >
                      Draft payment
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Other payment</CardTitle>
          <CardDescription>Pick any saved Wise recipient. The currency is the recipient's.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-2">
            <select
              className="h-9 rounded-md border px-2 text-sm min-w-[260px]"
              value={manual.recipient_id}
              onChange={(e) => setManual({ ...manual, recipient_id: e.target.value })}
            >
              <option value="">Recipient</option>
              {recipients.map((r) => (
                <option key={r.id} value={r.id}>{r.name} · {r.currency} · {r.account}</option>
              ))}
            </select>
            <Input className="w-32" placeholder="Amount" value={manual.amount} onChange={(e) => setManual({ ...manual, amount: e.target.value })} />
            <Input className="w-48" placeholder="Reference" maxLength={18} value={manual.reference} onChange={(e) => setManual({ ...manual, reference: e.target.value })} />
            <Button
              size="sm"
              disabled={!manual.recipient_id || !manual.amount || !manual.reference || busyId === 'manual'}
              onClick={async () => {
                await draft({ ...manual, recipient_id: Number(manual.recipient_id) }, 'manual');
                setManual({ recipient_id: '', amount: '', reference: '' });
              }}
            >
              Draft payment
            </Button>
          </div>
        </CardContent>
      </Card>

      {done.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Recent</CardTitle></CardHeader>
          <CardContent>
            <div className="divide-y text-sm">
              {done.map((d) => (
                <div key={d.id} className="flex items-center justify-between py-2">
                  <span>{d.supplier} · ref {d.reference} · {(d.paid_at || d.cancelled_at || '').slice(0, 10)}</span>
                  <span className="flex items-center gap-2">
                    <span className="tabular-nums">{formatCurrency(Number(d.amount), d.currency)}</span>
                    <Badge variant={STATUS[d.status]?.variant}>{STATUS[d.status]?.label}</Badge>
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
